import React, { useState, useEffect } from 'react';
import { projectFirestore } from '../../firebase/config';
import { CLOUDINARY_UPLOAD_PRESET, CLOUDINARY_UPLOAD_URL } from '../../cloudinary/config';

const SiteCustomizer = () => {
    const [settings, setSettings] = useState({ bannerUrl: '', bannerHistory: [], logoUrl: '', logoDarkUrl: '', videoUrl: '', siteTitle: '' });
    const [uploading, setUploading] = useState({});
    const [videoInput, setVideoInput] = useState('');
    const [titleInput, setTitleInput] = useState('');
    const [announcement, setAnnouncement] = useState({ enabled: false, text: '' });
    const [announcementInput, setAnnouncementInput] = useState('');

    useEffect(() => {
        const unsub1 = projectFirestore.collection('settings').doc('site')
            .onSnapshot((doc) => {
                if (doc.exists) {
                    const data = doc.data();
                    setSettings({
                        bannerUrl: data.bannerUrl || '',
                        bannerHistory: data.bannerHistory || [],
                        logoUrl: data.logoUrl || '',
                        logoDarkUrl: data.logoDarkUrl || '',
                        videoUrl: data.videoUrl || '',
                        siteTitle: data.siteTitle || '',
                    });
                    setVideoInput(data.videoUrl || '');
                    setTitleInput(data.siteTitle || '');
                }
            });
        const unsub2 = projectFirestore.collection('settings').doc('announcements')
            .onSnapshot((doc) => {
                if (doc.exists) {
                    const d = doc.data();
                    setAnnouncement({ enabled: d.enabled || false, text: d.text || '' });
                    setAnnouncementInput(d.text || '');
                }
            });
        return () => { unsub1(); unsub2(); };
    }, []);

    const uploadToCloudinary = (file, folder) => {
        return new Promise((resolve, reject) => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
            formData.append('folder', folder);

            const xhr = new XMLHttpRequest();
            xhr.open('POST', CLOUDINARY_UPLOAD_URL);
            xhr.onload = () => {
                if (xhr.status === 200) {
                    const res = JSON.parse(xhr.responseText);
                    resolve(res.secure_url);
                } else {
                    reject(new Error('فشل الرفع'));
                }
            };
            xhr.onerror = () => reject(new Error('خطأ في الاتصال'));
            xhr.send(formData);
        });
    };

    const handleBannerUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(prev => ({ ...prev, banner: true }));
        try {
            const url = await uploadToCloudinary(file, 'site-banners');
            const history = settings.bannerUrl
                ? [settings.bannerUrl, ...settings.bannerHistory.filter(h => h !== settings.bannerUrl)].slice(0, 10)
                : settings.bannerHistory;
            await projectFirestore.collection('settings').doc('site').set({
                ...settings,
                bannerUrl: url,
                bannerHistory: history,
            }, { merge: true });
        } catch (err) {
            alert('خطأ في رفع البانر: ' + err.message);
        }
        setUploading(prev => ({ ...prev, banner: false }));
        e.target.value = '';
    };

    const handleRestoreBanner = async (url) => {
        if (!window.confirm('هل تريد استعادة هذا البانر؟')) return;
        const history = [settings.bannerUrl, ...settings.bannerHistory.filter(h => h !== url && h !== settings.bannerUrl)].slice(0, 10);
        await projectFirestore.collection('settings').doc('site').set({
            ...settings,
            bannerUrl: url,
            bannerHistory: history,
        }, { merge: true });
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(prev => ({ ...prev, logo: true }));
        try {
            const url = await uploadToCloudinary(file, 'site-logos');
            await projectFirestore.collection('settings').doc('site').set({
                ...settings,
                logoUrl: url,
            }, { merge: true });
        } catch (err) {
            alert('خطأ في رفع اللوجو: ' + err.message);
        }
        setUploading(prev => ({ ...prev, logo: false }));
        e.target.value = '';
    };

    const handleDarkLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(prev => ({ ...prev, logoDark: true }));
        try {
            const url = await uploadToCloudinary(file, 'site-logos');
            await projectFirestore.collection('settings').doc('site').set({
                ...settings,
                logoDarkUrl: url,
            }, { merge: true });
        } catch (err) {
            alert('خطأ في رفع لوجو الوضع المظلم: ' + err.message);
        }
        setUploading(prev => ({ ...prev, logoDark: false }));
        e.target.value = '';
    };

    const handleVideoSave = async () => {
        await projectFirestore.collection('settings').doc('site').set({
            ...settings,
            videoUrl: videoInput,
        }, { merge: true });
        alert('تم حفظ رابط الفيديو ✓');
    };

    return (
        <div className="container my-4">
            <h4 className="text-center mb-4">تعديل واجهة الموقع</h4>

            {/* عنوان الموقع */}
            <div className="card shadow-sm mb-4">
                <div className="card-body">
                    <h5 className="card-title mb-3">عنوان الموقع</h5>
                    <p className="text-muted" style={{fontSize: 13}}>يظهر في الهيدر والفوتر والبانر وتبويب المتصفح</p>
                    <div className="input-group">
                        <input type="text" className="form-control" value={titleInput}
                            onChange={e => setTitleInput(e.target.value)}
                            placeholder="حكاية ضوء" />
                        <button className="btn btn-primary" onClick={async () => {
                            await projectFirestore.collection('settings').doc('site').set({ ...settings, siteTitle: titleInput.trim() }, { merge: true });
                            alert('تم حفظ العنوان ✓');
                        }}>حفظ</button>
                    </div>
                    {!titleInput && <small className="text-muted">الافتراضي: حكاية ضوء</small>}
                </div>
            </div>

            {/* شريط الإعلانات */}
            <div className="card shadow-sm mb-4">
                <div className="card-body">
                    <h5 className="card-title mb-3">شريط الإعلانات / الأخبار العاجلة</h5>
                    <div className="form-check form-switch mb-3">
                        <input className="form-check-input" type="checkbox" role="switch"
                            checked={announcement.enabled}
                            onChange={async (e) => {
                                const enabled = e.target.checked;
                                setAnnouncement(prev => ({ ...prev, enabled }));
                                await projectFirestore.collection('settings').doc('announcements').set({ ...announcement, enabled }, { merge: true });
                            }} />
                        <label className="form-check-label">{announcement.enabled ? 'مفعّل' : 'مخفي'}</label>
                    </div>
                    <div className="input-group">
                        <input type="text" className="form-control" value={announcementInput}
                            onChange={e => setAnnouncementInput(e.target.value)}
                            placeholder="نص الإعلان المتحرك..." />
                        <button className="btn btn-primary" onClick={async () => {
                            await projectFirestore.collection('settings').doc('announcements').set({
                                enabled: announcement.enabled,
                                text: announcementInput.trim(),
                            }, { merge: true });
                            alert('تم حفظ الإعلان ✓');
                        }}>حفظ</button>
                    </div>
                    {announcement.enabled && announcement.text && (
                        <div className="mt-2 p-2 rounded" style={{ background: 'linear-gradient(90deg, var(--primary), var(--primary-light))', color: 'white', fontSize: 13 }}>
                            معاينة: {announcement.text}
                        </div>
                    )}
                </div>
            </div>

            {/* البانر */}
            <div className="card shadow-sm mb-4">
                <div className="card-body">
                    <h5 className="card-title mb-3">البانر (صورة الغلاف العلوية)</h5>
                    {settings.bannerUrl && (
                        <div className="mb-3 text-center">
                            <img src={settings.bannerUrl} alt="البانر الحالي"
                                style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, objectFit: 'cover' }} />
                            <p className="text-muted mt-1" style={{ fontSize: 13 }}>البانر الحالي</p>
                        </div>
                    )}
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <label className="btn btn-outline-primary mb-0" style={{ cursor: 'pointer' }}>
                            {uploading.banner ? 'جاري الرفع...' : 'رفع بانر جديد'}
                            <input type="file" accept="image/*" hidden onChange={handleBannerUpload} disabled={uploading.banner} />
                        </label>
                        {settings.bannerUrl ? (
                            <button className="btn btn-outline-secondary btn-sm" onClick={async () => {
                                if (!window.confirm('هل تريد العودة للبانر الأصلي؟')) return;
                                const history = [settings.bannerUrl, ...settings.bannerHistory.filter(h => h !== settings.bannerUrl)].slice(0, 10);
                                await projectFirestore.collection('settings').doc('site').set({ ...settings, bannerUrl: '', bannerHistory: history }, { merge: true });
                            }}>
                                <i className="fas fa-undo me-1"></i> العودة للأصلي
                            </button>
                        ) : (
                            <span className="text-muted" style={{ fontSize: 13 }}>يُستخدم البانر الافتراضي (b.jpg)</span>
                        )}
                    </div>

                    {settings.bannerHistory.length > 0 && (
                        <div className="mt-3">
                            <p className="mb-2 fw-bold" style={{ fontSize: 14 }}>البانرات السابقة:</p>
                            <div className="d-flex gap-2 flex-wrap">
                                {settings.bannerHistory.map((url, i) => (
                                    <div key={i} className="text-center" style={{ cursor: 'pointer' }} onClick={() => handleRestoreBanner(url)}>
                                        <img src={url} alt={'بانر ' + (i + 1)}
                                            style={{ width: 120, height: 60, objectFit: 'cover', borderRadius: 6, border: '2px solid #ddd' }} />
                                        <small className="d-block text-primary">استعادة</small>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* اللوجو */}
            <div className="card shadow-sm mb-4">
                <div className="card-body">
                    <h5 className="card-title mb-3">اللوجو (الوضع العادي)</h5>
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                        {settings.logoUrl ? (
                            <img src={settings.logoUrl} alt="اللوجو الحالي"
                                style={{ width: 80, height: 60, objectFit: 'contain', background: '#f5f5f5', borderRadius: 8, padding: 4 }} />
                        ) : (
                            <img src="/logo.svg" alt="اللوجو الافتراضي"
                                style={{ width: 80, height: 60, objectFit: 'contain', background: '#f5f5f5', borderRadius: 8, padding: 4 }} />
                        )}
                        <label className="btn btn-outline-primary mb-0" style={{ cursor: 'pointer' }}>
                            {uploading.logo ? 'جاري الرفع...' : 'رفع لوجو جديد'}
                            <input type="file" accept="image/*" hidden onChange={handleLogoUpload} disabled={uploading.logo} />
                        </label>
                        {settings.logoUrl && (
                            <button className="btn btn-outline-secondary btn-sm" onClick={async () => {
                                await projectFirestore.collection('settings').doc('site').set({ ...settings, logoUrl: '' }, { merge: true });
                            }}>
                                العودة للوجو الافتراضي
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* لوجو الوضع المظلم */}
            <div className="card shadow-sm mb-4">
                <div className="card-body">
                    <h5 className="card-title mb-3">لوجو الوضع المظلم (اختياري)</h5>
                    <p className="text-muted" style={{fontSize: 13}}>إذا كان اللوجو الأساسي غامق ولا يظهر في الوضع المظلم، ارفع نسخة فاتحة هنا. إذا لم ترفع لوجو مظلم، سيتم إضاءة اللوجو الأساسي تلقائياً.</p>
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                        {settings.logoDarkUrl ? (
                            <img src={settings.logoDarkUrl} alt="لوجو مظلم"
                                style={{ width: 80, height: 60, objectFit: 'contain', background: '#1e1e1e', borderRadius: 8, padding: 4 }} />
                        ) : (
                            <div style={{ width: 80, height: 60, background: '#1e1e1e', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666', fontSize: 12 }}>
                                غير مخصص
                            </div>
                        )}
                        <label className="btn btn-outline-primary mb-0" style={{ cursor: 'pointer' }}>
                            {uploading.logoDark ? 'جاري الرفع...' : 'رفع لوجو الوضع المظلم'}
                            <input type="file" accept="image/*" hidden onChange={handleDarkLogoUpload} disabled={uploading.logoDark} />
                        </label>
                        {settings.logoDarkUrl && (
                            <button className="btn btn-outline-secondary btn-sm" onClick={async () => {
                                await projectFirestore.collection('settings').doc('site').set({ ...settings, logoDarkUrl: '' }, { merge: true });
                            }}>
                                إزالة
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* الفيديو */}
            <div className="card shadow-sm mb-4">
                <div className="card-body">
                    <h5 className="card-title mb-3">فيديو الصفحة الرئيسية</h5>
                    <div className="input-group">
                        <input type="text" className="form-control" value={videoInput}
                            onChange={e => setVideoInput(e.target.value)}
                            placeholder="رابط الفيديو (YouTube, Vimeo, ...)" dir="ltr" />
                        <button className="btn btn-primary" onClick={handleVideoSave}>حفظ</button>
                    </div>
                    {settings.videoUrl && (
                        <small className="text-muted mt-1 d-block">الرابط الحالي: {settings.videoUrl}</small>
                    )}
                </div>
            </div>

            {/* إعدادات الذكاء الاصطناعي — انتقلت إلى تبويب خاص */}
            <div className="card shadow-sm mb-4">
                <div className="card-body text-center">
                    <p className="text-muted mb-0">🤖 إعدادات الذكاء الاصطناعي انتقلت إلى تبويب <strong>"الذكاء الاصطناعي"</strong> في لوحة الإدارة.</p>
                </div>
            </div>
        </div>
    );
};

export default SiteCustomizer;
